/*
 * API client.
 *
 * Two quirks of the Flask API are absorbed here rather than at each call
 * site:
 *
 *  1. A 202 response means the work was queued. The LOCATION header holds a
 *     status URL which is polled until `status` is "finished" or "notfound";
 *     the finished payload is then returned in place of the 202.
 *
 *  2. Several endpoints signal failure with HTTP 200 and a body of
 *     `{result: "ERROR", message: "..."}` rather than an error status.
 *     Both are normalised into a thrown ApiError here, so callers only
 *     deal with one failure path.
 */

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 180000;

export class ApiError extends Error {
	constructor(message, { status, body } = {}) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.body = body;
	}
}

const sleep = (ms, signal) =>
	new Promise((resolve, reject) => {
		const timer = setTimeout(resolve, ms);
		signal?.addEventListener(
			'abort',
			() => {
				clearTimeout(timer);
				reject(new DOMException('Aborted', 'AbortError'));
			},
			{ once: true },
		);
	});

async function parseJson(response) {
	const text = await response.text();
	if (!text) return null;
	try {
		return JSON.parse(text);
	} catch {
		throw new ApiError('Server returned a malformed response.', {
			status: response.status,
			body: text.slice(0, 200),
		});
	}
}

/* Endpoints report failure in-band; surface it as an error. */
function assertNotInBandError(data) {
	if (data && typeof data === 'object' && data.result === 'ERROR') {
		throw new ApiError(data.message || 'Request failed.', { body: data });
	}
	return data;
}

async function pollUntilFinished(statusUrl, { signal } = {}) {
	const deadline = Date.now() + POLL_TIMEOUT_MS;

	while (Date.now() < deadline) {
		const response = await fetch(statusUrl, { signal, credentials: 'same-origin' });
		if (!response.ok) {
			throw new ApiError(`Polling failed (${response.status}).`, {
				status: response.status,
			});
		}
		const data = await parseJson(response);

		if (data?.status === 'finished') {
			// The finished job wraps its payload; fall back to the envelope
			// itself for endpoints that return the result inline.
			return assertNotInBandError(data.result ?? data.data ?? data);
		}
		if (data?.status === 'notfound') {
			throw new ApiError('The server could not find that job.', { body: data });
		}

		await sleep(POLL_INTERVAL_MS, signal);
	}

	throw new ApiError('Timed out waiting for the server to finish.');
}

async function request(url, { method = 'GET', body, signal } = {}) {
	const response = await fetch(url, {
		method,
		signal,
		credentials: 'same-origin',
		headers: body ? { 'Content-Type': 'application/json' } : undefined,
		body: body ? JSON.stringify(body) : undefined,
	});

	if (response.status === 202) {
		const location = response.headers.get('LOCATION') || response.headers.get('Location');
		if (!location) {
			throw new ApiError('Server queued the request but returned no status URL.', {
				status: 202,
			});
		}
		return pollUntilFinished(location, { signal });
	}

	if (!response.ok) {
		throw new ApiError(`Request failed (${response.status}).`, {
			status: response.status,
			body: await parseJson(response).catch(() => null),
		});
	}

	return assertNotInBandError(await parseJson(response));
}

export const api = {
	get: (url, opts) => request(url, { ...opts, method: 'GET' }),
	post: (url, body, opts) => request(url, { ...opts, method: 'POST', body }),
	delete: (url, body, opts) => request(url, { ...opts, method: 'DELETE', body }),
};

/* ------------------------------------------------------------------
 * Endpoints
 * ---------------------------------------------------------------- */

export function fetchManager({ managerId, username }, opts) {
	const params = new URLSearchParams();
	if (managerId) params.set('manager-id', managerId);
	else if (username) params.set('user', username);

	const query = params.toString();
	return api.get(`/api/manager${query ? `?${query}` : ''}`, opts);
}

export function saveManagerUsername({ managerId, username }, opts) {
	return api.post('/api/manager', { manager_id: managerId, username }, opts);
}

/*
 * Archived season for one manager. Unlike /api/manager this is a POST, and
 * the gameweek is chosen server-side — the whole payload is returned for the
 * requested gw, so navigation re-fetches rather than patching.
 */
export function fetchArchiveManager({ season, managerCode, gw }, opts) {
	return api.post(
		'/api/archive',
		{ season, manager_code: managerCode, ...(gw ? { gw } : {}) },
		opts,
	);
}

/* ------------------------------------------------------------------
 * Drafts
 *
 * The edit key is never a query parameter. core.py logs the full path of
 * every /api/ request, and lib/analytics.js reports window.location.href
 * to Google Analytics, so a key in a URL would end up in the application
 * log and at a third party. It travels only in an HttpOnly cookie the
 * server sets, which is why these calls rely on the shared
 * `credentials: 'same-origin'`.
 * ---------------------------------------------------------------- */

export function fetchDraftPool(opts) {
	return api.get('/api/draft/players', opts);
}

export function fetchDraft(slug, opts) {
	return api.get(`/api/draft/${encodeURIComponent(slug)}`, opts);
}

export function createDraft(draft, opts) {
	return api.post('/api/draft', draft, opts);
}

export function updateDraft(slug, draft, opts) {
	return api.post(`/api/draft/${encodeURIComponent(slug)}`, draft, opts);
}

export function deleteDraft(slug, opts) {
	return api.delete(`/api/draft/${encodeURIComponent(slug)}`, undefined, opts);
}

export function fetchGameweekPicks(
	{ managerId, playingPicks, benchPicks, gw },
	opts,
) {
	return api.post(
		'/api/fixtures',
		{
			manager_id: managerId,
			playing_picks: playingPicks,
			bench_picks: benchPicks,
			gw,
		},
		opts,
	);
}
